import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  WIZARD_STEPS,
  RISK_QUESTIONS,
  INDUSTRY_OPTIONS,
  createEmptyFormData,
  validateStep,
  buildMVAInput,
} from './liteQuestions';
import RiskQuestionStep from './RiskQuestionStep';
import { submitLiteValuation } from '../../services/api';
import './LiteWizard.css';

const DRAFT_KEY = 'lite_wizard_draft';
const DEBOUNCE_MS = 800;

/**
 * sanitizeDraft — allowlist-based merge of a raw parsed draft object.
 *
 * Only keys present in createEmptyFormData() are accepted, and their types
 * must match the empty shape. riskAnswers are validated individually:
 * only known RISK_QUESTIONS keys with integer values 0..4 are kept.
 * Everything else (injected keys, out-of-range values, wrong types) is dropped.
 */
function sanitizeDraft(raw) {
  const empty = createEmptyFormData();
  if (!raw || typeof raw !== 'object') return empty;
  const clean = { ...empty };
  Object.keys(empty).forEach((key) => {
    if (key === 'riskAnswers') return; // handled separately below
    if (raw[key] === undefined) return;
    // Special case: booleans — typeof check works cleanly
    // Special case: string fields with empty-string default — only accept strings
    // Special case: '' default fields also accept '' so type matches
    if (typeof raw[key] === typeof empty[key]) {
      clean[key] = raw[key];
    }
  });
  RISK_QUESTIONS.forEach(({ key }) => {
    const v = raw.riskAnswers ? raw.riskAnswers[key] : undefined;
    clean.riskAnswers[key] = Number.isInteger(v) && v >= 0 && v <= 4 ? v : null;
  });
  return clean;
}

/**
 * LiteWizard — 6-step public valuation wizard.
 * Props:
 *   onComplete(valuationId) — called after successful submit
 */
function LiteWizard({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(() => {
    // Restore draft on mount — run through allowlist sanitizer before use
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) {
        const draft = JSON.parse(saved);
        return sanitizeDraft(draft);
      }
    } catch (_) {
      // ignore corrupt draft
    }
    return createEmptyFormData();
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Prior-year disclosure state for step 2
  const [priorYearsOpen, setPriorYearsOpen] = useState(false);
  // Advanced disclosure state for step 6
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Debounced draft save
  const saveTimer = useRef(null);
  const saveDraft = useCallback((fd) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(fd));
      } catch (_) {}
    }, DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    saveDraft(formData);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [formData, saveDraft]);

  // ---- field change helpers ----

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleRiskAnswer = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      riskAnswers: { ...prev.riskAnswers, [key]: value },
    }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  // ---- navigation ----

  const handleNext = () => {
    const stepErrors = validateStep(currentStep, formData);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    setErrors({});
    setCurrentStep((s) => Math.max(0, s - 1));
  };

  // ---- submit ----

  const handleSubmit = async () => {
    // Validate the current (last) step first
    const lastStepErrors = validateStep(currentStep, formData);
    if (Object.keys(lastStepErrors).length > 0) {
      setErrors(lastStepErrors);
      return;
    }

    // Defense in depth: drafts or unexpected state mutations must not bypass
    // step gates. Re-validate every step and jump to the first failing one.
    for (let s = 0; s < WIZARD_STEPS.length; s++) {
      const stepErrors = validateStep(s, formData);
      if (Object.keys(stepErrors).length > 0) {
        setCurrentStep(s);
        setErrors(stepErrors);
        setSubmitError('Please fix the highlighted fields');
        return;
      }
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const input = buildMVAInput(formData);
      const result = await submitLiteValuation(input);
      // Clear the draft on success
      if (saveTimer.current) clearTimeout(saveTimer.current);
      localStorage.removeItem(DRAFT_KEY);
      onComplete(result.valuationId);
    } catch (err) {
      setSubmitting(false);
      if (err.response?.status === 400) {
        const data = err.response.data;
        const msgs = data.errors ? data.errors.join(' ') : (data.error || 'Invalid input.');
        setSubmitError(msgs);
      } else {
        setSubmitError('Network error — please check your connection and try again.');
      }
    }
  };

  // ---- step renderers ----

  const renderStep1 = () => (
    <div className="lite-wizard-step">
      <h2 className="lite-wizard-step-title">{WIZARD_STEPS[0].title}</h2>

      <div className="lite-wizard-field">
        <label htmlFor="lw-industry">Industry</label>
        <select
          id="lw-industry"
          value={formData.industry}
          onChange={(e) => handleChange('industry', e.target.value)}
          className={errors.industry ? 'lite-wizard-input--error' : ''}
        >
          <option value="">— choose industry —</option>
          {INDUSTRY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {errors.industry && <span className="lite-wizard-field-error">{errors.industry}</span>}
      </div>

      <div className="lite-wizard-field">
        <label htmlFor="lw-companyName">Company name (optional)</label>
        <input
          type="text"
          id="lw-companyName"
          value={formData.companyName}
          onChange={(e) => handleChange('companyName', e.target.value)}
          placeholder="e.g. Acme Co"
        />
      </div>

      <div className="lite-wizard-field">
        <label htmlFor="lw-foundingYear">Year founded (optional)</label>
        <input
          type="number"
          id="lw-foundingYear"
          value={formData.foundingYear}
          onChange={(e) => handleChange('foundingYear', e.target.value)}
          placeholder="e.g. 2010"
        />
      </div>

      <div className="lite-wizard-field">
        <label htmlFor="lw-employeeCount">Number of employees (optional)</label>
        <input
          type="number"
          id="lw-employeeCount"
          value={formData.employeeCount}
          onChange={(e) => handleChange('employeeCount', e.target.value)}
          placeholder="e.g. 12"
        />
      </div>

      <div className="lite-wizard-field">
        <label htmlFor="lw-location">Location (optional)</label>
        <input
          type="text"
          id="lw-location"
          value={formData.location}
          onChange={(e) => handleChange('location', e.target.value)}
          placeholder="e.g. Toronto, ON"
        />
      </div>

      <div className="lite-wizard-field lite-wizard-field--checkbox">
        <input
          type="checkbox"
          id="lw-canadianCompany"
          checked={formData.canadianCompany}
          onChange={(e) => handleChange('canadianCompany', e.target.checked)}
        />
        <label htmlFor="lw-canadianCompany">Is this a Canadian company?</label>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="lite-wizard-step">
      <h2 className="lite-wizard-step-title">{WIZARD_STEPS[1].title}</h2>

      <div className="lite-wizard-field">
        <label htmlFor="lw-lastFiscalYear">Last completed fiscal year</label>
        <input
          type="number"
          id="lw-lastFiscalYear"
          value={formData.lastFiscalYear}
          onChange={(e) => handleChange('lastFiscalYear', e.target.value)}
          placeholder="e.g. 2025"
          className={errors.lastFiscalYear ? 'lite-wizard-input--error' : ''}
        />
        {errors.lastFiscalYear && <span className="lite-wizard-field-error">{errors.lastFiscalYear}</span>}
      </div>

      <div className="lite-wizard-field">
        <label htmlFor="lw-ebitdaAnchor">Last year's operating profit — before interest, tax and depreciation (EBITDA)</label>
        <p className="lite-wizard-help-text">Roughly: profit before loan interest, tax, and any write-down of equipment value (depreciation/amortization). If your accountant prepares year-end financial statements, this number is usually on the income statement summary page.</p>
        <input
          type="number"
          id="lw-ebitdaAnchor"
          value={formData.ebitdaAnchor}
          onChange={(e) => handleChange('ebitdaAnchor', e.target.value)}
          placeholder="e.g. 1970000"
          className={errors.ebitdaAnchor ? 'lite-wizard-input--error' : ''}
        />
        {errors.ebitdaAnchor && <span className="lite-wizard-field-error">{errors.ebitdaAnchor}</span>}
      </div>

      <div className="lite-wizard-field">
        <label htmlFor="lw-sdeAnchor">Last year's total owner benefit — that profit, plus what the business paid you (SDE)</label>
        <p className="lite-wizard-help-text">Take the number above and add back your own salary, dividends, and any personal expenses the business covered (car, phone, travel, etc.). For many owner-run businesses, this is the more meaningful number — it shows the full financial benefit of owning the business.</p>
        <input
          type="number"
          id="lw-sdeAnchor"
          value={formData.sdeAnchor}
          onChange={(e) => handleChange('sdeAnchor', e.target.value)}
          placeholder="e.g. 2050000"
          className={errors.sdeAnchor ? 'lite-wizard-input--error' : ''}
        />
        {errors.sdeAnchor && <span className="lite-wizard-field-error">{errors.sdeAnchor}</span>}
      </div>

      <details
        className="lite-wizard-disclosure"
        open={priorYearsOpen}
        onToggle={(e) => setPriorYearsOpen(e.target.open)}
      >
        <summary className="lite-wizard-disclosure-summary">
          Add up to 4 earlier years (improves accuracy)
        </summary>
        {priorYearsOpen && (
          <div className="lite-wizard-disclosure-body">
            {[1, 2, 3, 4].map((n) => {
              const fy = parseInt(formData.lastFiscalYear, 10);
              const yearLabel = Number.isFinite(fy) ? String(fy - n) : `${n} year${n > 1 ? 's' : ''} earlier`;
              return (
                <div key={n} className="lite-wizard-prior-row">
                  <div className="lite-wizard-field">
                    <label htmlFor={`lw-ebitdaMinus${n}`}>EBITDA — {yearLabel}</label>
                    <input
                      type="number"
                      id={`lw-ebitdaMinus${n}`}
                      value={formData[`ebitdaMinus${n}`]}
                      onChange={(e) => handleChange(`ebitdaMinus${n}`, e.target.value)}
                      placeholder="optional"
                      className={errors[`ebitdaMinus${n}`] ? 'lite-wizard-input--error' : ''}
                    />
                    {errors[`ebitdaMinus${n}`] && (
                      <span className="lite-wizard-field-error">{errors[`ebitdaMinus${n}`]}</span>
                    )}
                  </div>
                  <div className="lite-wizard-field">
                    <label htmlFor={`lw-sdeMinus${n}`}>SDE — {yearLabel}</label>
                    <input
                      type="number"
                      id={`lw-sdeMinus${n}`}
                      value={formData[`sdeMinus${n}`]}
                      onChange={(e) => handleChange(`sdeMinus${n}`, e.target.value)}
                      placeholder="optional"
                      className={errors[`sdeMinus${n}`] ? 'lite-wizard-input--error' : ''}
                    />
                    {errors[`sdeMinus${n}`] && (
                      <span className="lite-wizard-field-error">{errors[`sdeMinus${n}`]}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </details>
    </div>
  );

  const renderStep3 = () => (
    <div className="lite-wizard-step">
      <h2 className="lite-wizard-step-title">{WIZARD_STEPS[2].title}</h2>

      <div className="lite-wizard-field">
        <label htmlFor="lw-totalAssets">Total assets ($)</label>
        <input
          type="number"
          id="lw-totalAssets"
          value={formData.totalAssets}
          onChange={(e) => handleChange('totalAssets', e.target.value)}
          placeholder="e.g. 2500000"
          className={errors.totalAssets ? 'lite-wizard-input--error' : ''}
        />
        {errors.totalAssets && <span className="lite-wizard-field-error">{errors.totalAssets}</span>}
      </div>

      <div className="lite-wizard-field">
        <label htmlFor="lw-totalLiabilities">Total liabilities ($)</label>
        <input
          type="number"
          id="lw-totalLiabilities"
          value={formData.totalLiabilities}
          onChange={(e) => handleChange('totalLiabilities', e.target.value)}
          placeholder="e.g. 600000"
          className={errors.totalLiabilities ? 'lite-wizard-input--error' : ''}
        />
        {errors.totalLiabilities && <span className="lite-wizard-field-error">{errors.totalLiabilities}</span>}
      </div>

      <div className="lite-wizard-field">
        <label htmlFor="lw-inventory">Inventory ($, optional)</label>
        <input
          type="number"
          id="lw-inventory"
          value={formData.inventory}
          onChange={(e) => handleChange('inventory', e.target.value)}
          placeholder="e.g. 200000"
          className={errors.inventory ? 'lite-wizard-input--error' : ''}
        />
        {errors.inventory && <span className="lite-wizard-field-error">{errors.inventory}</span>}
      </div>
    </div>
  );

  const renderRiskStep = (stepIndex) => {
    const step = WIZARD_STEPS[stepIndex];
    const questions = RISK_QUESTIONS.filter((q) => step.questionKeys.includes(q.key));
    return (
      <div className="lite-wizard-step">
        <h2 className="lite-wizard-step-title">{step.title}</h2>
        <p className="lite-wizard-step-sub">
          For each question, pick the description that best matches your business.
        </p>
        <RiskQuestionStep
          questions={questions}
          answers={formData.riskAnswers}
          errors={errors}
          onAnswer={handleRiskAnswer}
        />
      </div>
    );
  };

  const renderStep6 = () => {
    const answeredCount = RISK_QUESTIONS.filter(
      (q) => Number.isInteger(formData.riskAnswers[q.key])
    ).length;

    return (
      <div className="lite-wizard-step">
        <h2 className="lite-wizard-step-title">{WIZARD_STEPS[5].title}</h2>

        <div className="lite-wizard-recap">
          <div className="lite-wizard-recap-row">
            <span>Industry</span>
            <span>{formData.industry || '—'}</span>
          </div>
          <div className="lite-wizard-recap-row">
            <span>EBITDA (anchor year)</span>
            <span>{formData.ebitdaAnchor ? `$${Number(formData.ebitdaAnchor).toLocaleString()}` : '—'}</span>
          </div>
          <div className="lite-wizard-recap-row">
            <span>SDE (anchor year)</span>
            <span>{formData.sdeAnchor ? `$${Number(formData.sdeAnchor).toLocaleString()}` : '—'}</span>
          </div>
          <div className="lite-wizard-recap-row">
            <span>Total assets</span>
            <span>{formData.totalAssets ? `$${Number(formData.totalAssets).toLocaleString()}` : '—'}</span>
          </div>
          <div className="lite-wizard-recap-row">
            <span>Total liabilities</span>
            <span>{formData.totalLiabilities ? `$${Number(formData.totalLiabilities).toLocaleString()}` : '—'}</span>
          </div>
          <div className="lite-wizard-recap-row">
            <span>Risk questions answered</span>
            <span>{answeredCount} / {RISK_QUESTIONS.length}</span>
          </div>
        </div>

        <details
          className="lite-wizard-disclosure"
          open={advancedOpen}
          onToggle={(e) => setAdvancedOpen(e.target.open)}
        >
          <summary className="lite-wizard-disclosure-summary">Advanced (optional)</summary>
          {advancedOpen && (
            <div className="lite-wizard-disclosure-body">
              <div className="lite-wizard-field">
                <label htmlFor="lw-ruleOfThumbMultiple">Rule-of-thumb multiple</label>
                <input
                  type="number"
                  id="lw-ruleOfThumbMultiple"
                  value={formData.ruleOfThumbMultiple}
                  onChange={(e) => handleChange('ruleOfThumbMultiple', e.target.value)}
                  placeholder="3.0 — industry default"
                  step="0.1"
                  min="0.1"
                  max="15"
                  className={errors.ruleOfThumbMultiple ? 'lite-wizard-input--error' : ''}
                />
                {errors.ruleOfThumbMultiple && (
                  <span className="lite-wizard-field-error">{errors.ruleOfThumbMultiple}</span>
                )}
              </div>
              <div className="lite-wizard-field lite-wizard-field--checkbox">
                <input
                  type="checkbox"
                  id="lw-addInventory"
                  checked={formData.addInventory}
                  onChange={(e) => handleChange('addInventory', e.target.checked)}
                />
                <label htmlFor="lw-addInventory">Add inventory to valuation</label>
              </div>
            </div>
          )}
        </details>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0: return renderStep1();
      case 1: return renderStep2();
      case 2: return renderStep3();
      case 3: return renderRiskStep(3);
      case 4: return renderRiskStep(4);
      case 5: return renderStep6();
      default: return null;
    }
  };

  const isLastStep = currentStep === WIZARD_STEPS.length - 1;
  const totalSteps = WIZARD_STEPS.length;
  const progressPct = ((currentStep + 1) / totalSteps) * 100;

  return (
    <div className="lite-wizard">
      {/* Progress */}
      <div className="lite-wizard-progress">
        <div className="lite-wizard-progress-bar">
          <div
            className="lite-wizard-progress-fill"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <p className="lite-wizard-progress-text">Step {currentStep + 1} of {totalSteps}</p>
      </div>

      {/* Submit error banner */}
      {submitError && (
        <div className="lite-wizard-error-banner" role="alert">
          <p>{submitError}</p>
        </div>
      )}

      {/* Step body */}
      <div className="lite-wizard-body">
        {renderCurrentStep()}
      </div>

      {/* Navigation */}
      <div className="lite-wizard-actions">
        <button
          type="button"
          className="lite-wizard-btn lite-wizard-btn--secondary"
          onClick={handleBack}
          disabled={currentStep === 0}
        >
          Back
        </button>

        {isLastStep ? (
          <button
            type="button"
            className="lite-wizard-btn lite-wizard-btn--primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Calculating…' : 'Calculate my valuation'}
          </button>
        ) : (
          <button
            type="button"
            className="lite-wizard-btn lite-wizard-btn--primary"
            onClick={handleNext}
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
}

export default LiteWizard;
